/**
 * 投稿アクション (like / repost / bookmark) を fetch ベースに変えて、
 * 全画面リロードなしで toggle + アニメーション + count 更新 + toast を出す。
 *
 * 既存テンプレートの <form action="/like/{id}" method="post">... をそのまま使う。
 * fetch 後はサーバが {ok, on, count, kind} を JSON で返してくれる前提
 * (write.go の ReactionHandler が AJAX 判定で JSON を返す)。
 */

import { showToast } from "./toast";
import { handleBlock } from "./limits";
import { u } from "./url";

interface ReactionRes {
  ok: boolean;
  on: boolean;
  count: number;
  kind: string;
  /** 読み合いが成立した相手の名前 (2026-09-17)。**成立した瞬間だけ** 入る。 */
  mutual?: string;
}

const KIND_BY_PATH: Record<string, "like" | "repost" | "bookmark"> = {
  "/like/": "like",
  "/repost/": "repost",
  "/bookmark/": "bookmark",
};

function findKind(action: string): "like" | "repost" | "bookmark" | null {
  for (const prefix of Object.keys(KIND_BY_PATH)) {
    if (action.includes(prefix)) return KIND_BY_PATH[prefix];
  }
  return null;
}

export function initInteractions(): void {
  // いいねの初回だけの確認 (2026-09-16 監査 P-015)。morioka-feedback.js の演出は
  // document の capture で click を拾うので、それより先 (window の capture) で止めないと
  // 「やめる」を押しても光ってしまう。確認のあとは演出を手で出してから送信する。
  window.addEventListener(
    "click",
    (e) => {
      const t = e.target instanceof Element ? e.target : null;
      const btn = t ? t.closest<HTMLButtonElement>("form.inline-form button.action-btn.like") : null;
      if (!btn || !needsLikeConfirm(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      openLikeConfirm(btn);
    },
    true,
  );

  // 画面が変わったらシートを閉じる (softnav の .col-main 差し替え / 戻る)。
  document.addEventListener("mp:navigated", closeLikeConfirm);
  window.addEventListener("popstate", closeLikeConfirm);

  document.addEventListener("submit", async (e) => {
    const form = e.target as HTMLFormElement;
    if (!form || !form.matches("form.inline-form")) return;
    const action = form.action || "";
    const kind = findKind(action);
    if (!kind) return;

    e.preventDefault();
    const btn = form.querySelector<HTMLButtonElement>("button.action-btn");
    if (!btn) return;
    // click を経ない送信 (キーボードの暗黙送信・requestSubmit) でも初回の確認を飛ばさない。
    if (kind === "like" && needsLikeConfirm(btn)) {
      openLikeConfirm(btn);
      return;
    }
    const icon = btn.querySelector<HTMLElement>("i");
    const countEl = btn.querySelector<HTMLElement>(".count");

    // 二重送信防止
    if (btn.disabled) return;
    btn.disabled = true;

    // 楽観 UI: 押した瞬間に演出を出す (解除クリックでは何もしない = X と同じ)。
    // サーバ応答を待たないので、通信が遅くても指の動きに即応する。
    // 発光/リング/スパーク/アイコンの跳ねは morioka-feedback.js (v5) が
    // data-feedback 属性の委譲クリックで既に出している。ここで重ねない。
    if (!btn.classList.contains(STATE_CLASS[kind])) {
      btn.classList.add("is-popping");
      setTimeout(() => btn.classList.remove("is-popping"), 480);
    }

    const fd = new FormData(form);
    try {
      const res = await fetch(action, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json", "X-Requested-With": "fetch" },
        credentials: "same-origin",
      });
      if (!res.ok) {
        // 402 = いいね の守が足りない (いいね = 訊価の 3 倍、2026-09-09)。サーバは
        // {ok:false, insufficient:true, error:"守が足りません。いいねには N 守が要ります…"}
        // を返し、いいねは付いていない (TX ごと巻き戻し済み) ので UI も変えない。
        if (res.status === 402) {
          let msg = "守が足りません";
          try {
            const j = await res.json();
            if (j && typeof j.error === "string" && j.error) msg = j.error;
          } catch {
            /* 本文なし */
          }
          // 次の一手 (書いて稼ぐ) を添える。押す時間があるように少し長めに出す。
          showToast(msg, "warn", { duration: 6000 });
          attachEarnLink();
          return;
        }
        // 401 / 403 / 429 — ログイン切れ・凍結・一時制限・低速モード・連打。
        // ⚠ ここをまとめて「ログインが必要です」にしない (2026-09-21 オーナー報告)。
        //    種類ごとの 1 行はサーバが作る (internal/moderation/limit.go)。
        if (await handleBlock(res)) return;
        throw new Error("HTTP " + res.status);
      }
      const data: ReactionRes = await res.json();
      // UI 更新
      applyReactionState(btn, icon, countEl, kind, data);
      // 「いいねしました」「リポストしました」等の確認 toast は廃止 (ユーザ要望)。
      // アニメ + アイコン色変化で十分なフィードバックになっており、毎タップで
      // 文字が出ると煩い。bookmark は元から silent なので変更なし。
      // いいね は守が動く (訊価の 3 倍) ので、ヘッダーの守残高チップに取りに行かせる
      // (変わった桁だけが回る = 文字を出さずに「払った」が分かる)。
      if (kind === "like" && data.on) {
        document.dispatchEvent(new Event("mori:refresh"));
        // 読み合いが成立した瞬間だけ、一度きり祝う (2026-09-17)。
        // ⚠ 借りを指摘することはしない。出すのは成立した側から。
        if (data.mutual) showToast(data.mutual + "さんと読み合っています", "success");
        // 払うのは 1 投稿 1 回 (解除 → 再いいね は無料)。額の案内はもう要らない。
        if (btn.hasAttribute("data-like-cost")) {
          btn.removeAttribute("data-like-cost");
          btn.title = "いいね";
        }
      }
    } catch (err) {
      console.error("[interactions] failed", err);
      showToast("操作に失敗しました", "error");
    } finally {
      btn.disabled = false;
    }
  });
}

/** 状態クラス。kind → 「オンのとき付くクラス」。 */
const STATE_CLASS: Record<"like" | "repost" | "bookmark", string> = {
  like: "liked",
  repost: "reposted",
  bookmark: "bookmarked",
};

/** カウンタの数字を X 風に転がす (古い数字が上へ抜け、新しい数字が下から入る)。 */
function rollCount(el: HTMLElement, next: string): void {
  const prev = el.textContent || "";
  if (prev === next) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !prev || !next) {
    el.textContent = next;
    return;
  }
  const up = Number(next) > Number(prev); // 増加なら下から、減少なら上から
  el.classList.add("is-rolling");
  el.innerHTML = "";
  const oldSpan = document.createElement("span");
  oldSpan.className = "cnt-out";
  oldSpan.textContent = prev;
  const newSpan = document.createElement("span");
  newSpan.className = "cnt-in";
  newSpan.textContent = next;
  el.classList.toggle("roll-down", !up);
  el.append(oldSpan, newSpan);
  setTimeout(() => {
    el.classList.remove("is-rolling", "roll-down");
    el.textContent = next;
  }, 300);
}

declare global {
  interface Window {
    /** php/app.js の toggleRepost からも使う (生 JS なので window 経由)。 */
    mpRollCount?: (el: HTMLElement, next: string) => void;
  }
}
window.mpRollCount = rollCount;

function applyReactionState(
  btn: HTMLButtonElement,
  icon: HTMLElement | null,
  countEl: HTMLElement | null,
  kind: "like" | "repost" | "bookmark",
  data: ReactionRes,
): void {
  btn.classList.toggle(STATE_CLASS[kind], data.on);

  if (icon) {
    // heart / bookmark は regular ↔ solid 切替、retweet は色だけなので class 操作不要
    if (kind === "like" || kind === "bookmark") {
      icon.classList.toggle("fa-regular", !data.on);
      icon.classList.toggle("fa-solid", data.on);
    }
  }
  if (countEl) {
    rollCount(countEl, data.count > 0 ? String(data.count) : "");
  }
}

// ---------------------------------------------------------------------------
// いいねの初回だけの確認 (2026-09-16 監査 P-015、ヘルプ reactions#like-cost)
// ---------------------------------------------------------------------------
//
// いいね = 訊価の 3 倍 (追加で 2 倍)・解除しても返金なし。カードに額は出さない
// (オーナー指定) ので、スマホでは押す前に額が分からない。**そのアカウントで初めて
// 守の動くいいねを押すときだけ** 小さなシートで額を見せ、OK で印を立てて送る。
//
//   - 出す条件: data-like-cost (追加で払う額) が 1 以上 かつ まだ いいね していない
//     かつ localStorage の印 (mp_like_cost_ack) が無い。
//   - localStorage が使えない環境 (プライベートブラウズ・ブロック) では出さずに
//     従来どおり送る (印を覚えられないので毎回出てしまうため)。
//   - シートは .mp-sheet-host の器を借りる (body.mp-sheet-open で引っ張って更新と
//     横スワイプが arm しない = pull_refresh.ts / hswipe.ts の既存の除外に乗る)。
//   - opacity は使わない (theme-light の全称ルールに潰される)。v2.css §231。

/** 初回確認を済ませた印。ユーザーごとではなく端末 (ブラウザ) ごと。 */
const LIKE_ACK_KEY = "mp_like_cost_ack";

function storageOK(): boolean {
  try {
    const k = "__mp_ls_probe";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

function likeCostOf(btn: HTMLElement): number {
  const n = Number(btn.getAttribute("data-like-cost") || "0");
  return Number.isFinite(n) ? n : 0;
}

function needsLikeConfirm(btn: HTMLElement): boolean {
  if (likeCostOf(btn) < 1) return false;
  if (btn.classList.contains("liked")) return false; // 解除は守が動かない
  if (!storageOK()) return false;
  try {
    return window.localStorage.getItem(LIKE_ACK_KEY) === null;
  } catch {
    return false;
  }
}

/** title 属性「訊価の N 倍 = 追加で M 守」から倍率を読む (post_actions.html が持つ唯一の場所)。 */
function likeMultOf(btn: HTMLElement): number {
  const m = /訊価の\s*(\d+)\s*倍/.exec(btn.getAttribute("title") || "");
  return m ? Number(m[1]) : 0;
}

let likeSheet: HTMLElement | null = null;
let likeTarget: HTMLButtonElement | null = null;
let likeCloseTimer: number | null = null;
const LIKE_SHEET_CLOSE_MS = 260;

function ensureLikeSheet(): HTMLElement {
  if (likeSheet && likeSheet.isConnected) return likeSheet;
  const host = document.createElement("div");
  host.className = "mp-sheet-host lk-confirm";
  host.hidden = true;
  host.innerHTML =
    '<div class="mp-sheet-backdrop" data-lk-cancel></div>' +
    '<div class="mp-sheet-panel lk-confirm-panel" role="dialog" aria-modal="true" aria-labelledby="lkConfirmTitle" aria-describedby="lkConfirmText">' +
    '<div class="mp-sheet-grabber" aria-hidden="true"></div>' +
    '<div class="lk-confirm-body">' +
    '<p class="lk-confirm-title" id="lkConfirmTitle"></p>' +
    '<p class="lk-confirm-text" id="lkConfirmText"></p>' +
    "</div>" +
    '<div class="lk-confirm-actions">' +
    '<button type="button" class="lk-confirm-ok" data-lk-ok></button>' +
    '<button type="button" class="mp-sheet-cancel lk-confirm-cancel" data-lk-cancel>やめる</button>' +
    "</div>" +
    "</div>";
  host.addEventListener("click", (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;
    if (t.closest("[data-lk-ok]")) {
      confirmLike();
    } else if (t.closest("[data-lk-cancel]")) {
      closeLikeConfirm();
    }
  });
  document.body.appendChild(host);
  likeSheet = host;
  return host;
}

function openLikeConfirm(btn: HTMLButtonElement): void {
  const host = ensureLikeSheet();
  const extra = likeCostOf(btn);
  const mult = likeMultOf(btn);
  likeTarget = btn;
  const title = host.querySelector<HTMLElement>(".lk-confirm-title");
  const text = host.querySelector<HTMLElement>(".lk-confirm-text");
  const ok = host.querySelector<HTMLButtonElement>("[data-lk-ok]");
  if (title) {
    title.textContent = mult > 1 ? `いいねは訊価の ${mult} 倍 (追加で ${extra} 守)` : `いいねすると追加で ${extra} 守`;
  }
  if (text) {
    text.textContent = "書いた人に守が渡ります。解除しても戻りません。確認はこの 1 回だけです。";
  }
  if (ok) ok.textContent = `いいねする (${extra} 守)`;
  if (likeCloseTimer !== null) {
    window.clearTimeout(likeCloseTimer);
    likeCloseTimer = null;
  }
  host.classList.remove("is-closing");
  host.hidden = false;
  document.body.classList.add("mp-sheet-open");
  void host.offsetWidth; // hidden 解除と同じフレームだと transition が走らない
  host.classList.add("is-open");
  document.addEventListener("keydown", onLikeSheetKey, true);
  if (ok) ok.focus();
}

function closeLikeConfirm(): void {
  const host = likeSheet;
  const back = likeTarget;
  likeTarget = null;
  document.removeEventListener("keydown", onLikeSheetKey, true);
  if (!host || host.hidden) return;
  host.classList.remove("is-open");
  host.classList.add("is-closing");
  const finish = (): void => {
    likeCloseTimer = null;
    host.hidden = true;
    host.classList.remove("is-closing");
    document.body.classList.remove("mp-sheet-open");
  };
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    finish();
  } else {
    likeCloseTimer = window.setTimeout(finish, LIKE_SHEET_CLOSE_MS);
  }
  if (back && back.isConnected) back.focus({ preventScroll: true });
}

function onLikeSheetKey(e: KeyboardEvent): void {
  if (e.key === "Escape") {
    e.preventDefault();
    closeLikeConfirm();
  }
}

function confirmLike(): void {
  const btn = likeTarget;
  try {
    window.localStorage.setItem(LIKE_ACK_KEY, String(Date.now()));
  } catch {
    /* 印を書けなくても今回は送る (needsLikeConfirm が storageOK で次から出さない) */
  }
  closeLikeConfirm();
  if (!btn || !btn.isConnected) return;
  const form = btn.closest<HTMLFormElement>("form.inline-form");
  if (!form) return;
  // 止めた click の代わりに、確定した いいね の演出をここで出す (解除では出さない)。
  const fb = (window as unknown as { MoriokaFeedback?: { trigger(type: string, el: Element): void } }).MoriokaFeedback;
  if (fb && typeof fb.trigger === "function") fb.trigger("like", btn);
  if (typeof form.requestSubmit === "function") {
    form.requestSubmit(btn);
  } else {
    form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
  }
}

/**
 * 402 (守が足りない) のトーストに「投稿して稼ぐ」を添える。toast.ts は文字しか持たない
 * ので、いま出したピルの末尾にリンクを足す。押すとコンポーザが開く (data-composer-trigger。
 * JS が効かなければ /?compose=1 で composer.ts が開く)。
 */
function attachEarnLink(): void {
  const stack = document.getElementById("mpToastStack");
  const toast = stack ? stack.lastElementChild : null;
  const inner = toast ? toast.querySelector<HTMLElement>(".mp-toast-inner") : null;
  if (!inner || inner.querySelector(".mp-toast-earn")) return;
  const a = document.createElement("a");
  a.className = "mp-toast-earn";
  a.href = u("/?compose=1");
  a.setAttribute("data-composer-trigger", '{"mode":"new"}');
  a.textContent = "投稿して稼ぐ";
  inner.appendChild(a);
}
